import moment from 'moment';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import usePersistentState from '../use-persistent-state';
import { parseTokenPair } from '../exotics-tokens/use-available-tokens';
import { useListedExpiries } from '../exotics-tokens/use-listed-expiries';
import { WebSocketContext } from '../websocket-context/websocket-provider';

import { MarketChangeContext } from './market-change-provider';

// IM-2599 - Market Change options-chain data layer (per-strike dIV, T1 vs T2).
//
// Self-contained historic fetch mirroring market-change-surface-provider: it reads
// the shared selection (market, T1, T2) from MarketChangeContext plus a selected
// listed EXPIRY, and produces a per-strike implied-vol chain at each timestamp and
// their difference. Each selection is a GENERATION (`${KEY}_${slot}_${gen}`); the
// handler drops any reply whose generation is not current, so an out-of-order WS
// response for a superseded selection can never write a stale chain. The prior
// generation's channels are torn down before the next opens and on unmount.
//
// Three requests per generation, sequenced because the IVS call needs a strike
// list: ONE live `option_open_interest` (deribit) yields the listed strike
// universe for the selected expiry, then TWO historic `implied_volatility_smile`
// calls (T1, T2) at those strikes. dOI is DROPPED for v1 (per-strike OI is
// live-only; mixing it with historic IV misleads) - OI is used ONLY to source the
// strike set to query, never rendered.
//
// TWO correctness gates on every IVS response (both pure, unit-tested):
//  1. Echo guard - the backend returns the NEAREST-AVAILABLE snapshot for a
//     recent/now request, so result.surface.expiry.timestamp legitimately differs
//     (by format, or by a genuine snap to the nearest stored minute) from the
//     requested value_date. We never hard-discard an endpoint for that: the
//     returned timestamp is used as that endpoint's actual as-of time and shown to
//     the user. The one signal still worth distrusting is value_date being
//     IGNORED outright - which shows up as T1 and T2 echoing back the SAME
//     timestamp despite being requested at different minutes (sameInstant /
//     chainEndpointsSuspect below); that blanks the dIV and surfaces an error.
//  2. Calibration gate - if the requested expiry is ABSENT from
//     result.surface.expiry.calibrations the API has SILENTLY interpolated a smile
//     (it does not error), so that endpoint's IV is untrusted: its cells AND the
//     dIV are blanked. Never show a difference derived from an interpolated side.
//
// Vols arrive as FRACTIONS; the view multiplies by 100 for display. An absent
// strike/cell is ABSENT (finite checks), never zero.

export type ChainSlot = 'T1' | 'T2';

const KEY = 'MARKET_CHANGE_CHAIN';
const STATE_PATH = ['MARKET_CHANGE_CHAIN'];

// Live-T2 repoll cadence, matching use-implied-vol-smile's POLL_MS.
const POLL_MS = 30_000;

// Deribit option expiries settle 08:00 UTC; the IVS `expiry` param is a datetime,
// while the listed-expiry chain and the calibration-gate keys are bare dates.
const EXPIRY_SETTLE_TIME = 'T08:00:00';

// One side (call or put) of a strike's instrument detail: price in BTC
// (`pv_und`), price in USD (`pv_acc`) and `delta`, each read independently off
// the greek dict - a field that is absent or non-finite is simply absent from
// this object, never defaulted to 0.
export interface ChainSide {
  priceBtc?: number;
  priceUsd?: number;
  delta?: number;
}

// One strike's parsed data: the per-strike IV (fraction) plus the call/put
// instrument detail read from the greek dicts. `iv` drives the v1 table
// columns; `call`/`put` back the IM-2599 detail block (never rendered as
// table columns - see the panel's comment).
export interface ChainEntry {
  iv: number;
  call?: ChainSide;
  put?: ChainSide;
}

// strike (as a string key) -> entry. Keyed by String(strike) so the SAME requested
// strike aligns across the two endpoints (both are sent the identical strike list).
export type ChainStrikes = Record<string, ChainEntry>;

// One timestamp's chain. `trusted` is the calibration-gate verdict for THIS
// endpoint: when false the smile was interpolated, so the view blanks its IV and
// the dIV. `timestamp` is whatever this endpoint actually echoed back - the
// nearest-available snapshot, which may differ from the requested minute.
export interface ChainEndpoint {
  timestamp: string;
  trusted: boolean;
  strikes: ChainStrikes;
  // result.market.spot off this same response, when finite - used to highlight
  // the strike nearest spot in the chain. Absent (never 0) when the field is
  // missing or non-finite.
  spot?: number;
}

interface IMarketChangeChainProvider {
  endpointT1: ChainEndpoint | null;
  endpointT2: ChainEndpoint | null;
  // Per-strike IV(T2) - IV(T1), fractions, only where BOTH sides are trusted and
  // finite. null when either side is untrusted/absent (never a spurious diff).
  chainDelta: Record<string, number> | null;
  // Ordered (ascending) strike-key universe for the rows, contributed only by
  // trusted endpoints.
  strikeRows: string[];
  expiries: string[];
  selectedExpiry: string;
  setSelectedExpiry: (expiry: string) => void;
  // 'generated' surfaces carry no instrument chain - the panel placeholders and we
  // never fetch. undefined while the registry is resolving.
  surfaceType: 'exchange' | 'generated' | undefined;
  loading: boolean;
  error: string | null;
}

interface IMarketChangeChainParamsProvider {
  children?: ReactNode;
}

export const MarketChangeChainContext = createContext({} as IMarketChangeChainProvider);

// ISO timestamp -> the backend's historic value_date request format
// "YYYY-MM-DD HH:MM:SS" (UTC). The RESPONSE does not necessarily echo this exact
// string back (see the echo-guard note above - a recent/now request can snap to
// the nearest available minute), so this is a request-side formatter only.
export function formatValueDate(iso: string): string {
  return moment(iso).utc().format('YYYY-MM-DD HH:mm:ss');
}

// Listed-expiry date ("YYYY-MM-DD") -> the IVS `expiry` datetime param (08:00 UTC
// settle), e.g. "2026-10-30" -> "2026-10-30T08:00:00".
export function expiryToParam(expiryDate: string): string {
  return `${expiryDate}${EXPIRY_SETTLE_TIME}`;
}

// A leaf/option-map expiry (ISO date, ISO datetime, or a Deribit code like
// '30OCT26') -> "YYYY-MM-DD", or null when unparseable. The live
// option_open_interest leaf.expiry string format is not in the captured sample, so
// both the ISO prefix and the exchange code are handled; extend here if a venue
// uses another form.
function normalizeExpiryDate(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw) return null;
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const m = moment.utc(raw, ['DDMMMYY', 'DDMMMYYYY', moment.ISO_8601], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

// The listed strike universe to QUERY, drawn from a live option_open_interest
// result for the selected expiry. Walks venue -> token -> symbol leaves (venue/
// token keys are not assumed), reads each leaf's numeric `strike` where its
// (normalized) expiry matches, and returns the sorted unique strikes. A non-finite
// strike is skipped (absent, never 0). PURE.
export function selectExpiryStrikes(result: any, expiryDate: string): number[] {
  const oi = result?.open_interest;
  if (!oi || typeof oi !== 'object') return [];

  const strikes = new Set<number>();
  for (const venue of Object.values<any>(oi)) {
    if (!venue || typeof venue !== 'object') continue;
    for (const tokenMap of Object.values<any>(venue)) {
      if (!tokenMap || typeof tokenMap !== 'object') continue;
      for (const [expiryKey, symbolMap] of Object.entries<any>(tokenMap)) {
        if (!symbolMap || typeof symbolMap !== 'object') continue;
        for (const leaf of Object.values<any>(symbolMap)) {
          if (!leaf || typeof leaf !== 'object') continue;
          if (normalizeExpiryDate(leaf.expiry ?? expiryKey) !== expiryDate) continue;
          const strike = typeof leaf.strike === 'number' ? leaf.strike : Number(leaf.strike);
          if (Number.isFinite(strike)) strikes.add(strike);
        }
      }
    }
  }
  return Array.from(strikes).sort((a, b) => a - b);
}

// One greek dict (keyed "<INSTRUMENT>-C" / "-P") -> a ChainSide, or undefined
// when none of its three fields are usable. Each field is read and finite-
// checked independently - e.g. a side with a price but no delta yet still
// carries the price. PURE.
function parseChainSide(greeks: any): ChainSide | undefined {
  if (!greeks || typeof greeks !== 'object') return undefined;

  const side: ChainSide = {};
  const priceBtc = greeks.pv_und;
  if (typeof priceBtc === 'number' && Number.isFinite(priceBtc)) side.priceBtc = priceBtc;
  const priceUsd = greeks.pv_acc;
  if (typeof priceUsd === 'number' && Number.isFinite(priceUsd)) side.priceUsd = priceUsd;
  const delta = greeks.delta;
  if (typeof delta === 'number' && Number.isFinite(delta)) side.delta = delta;

  return Object.keys(side).length > 0 ? side : undefined;
}

// Parse an implied_volatility_smile result's `strikes` array into strike -> entry.
// `volatility` is the per-strike IV; the two non-strike/non-volatility keys are the
// call/put greek dicts, split by the key's '-C'/'-P' suffix (the instrument-name
// key is CONSTRUCTED, never matched literally). A row with a non-finite strike or
// IV is dropped (absent, never 0). PURE.
export function parseChainStrikes(result: any): ChainStrikes | null {
  const rows = result?.strikes;
  if (!Array.isArray(rows)) return null;

  const out: ChainStrikes = {};
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const strike = typeof row.strike === 'number' ? row.strike : Number(row.strike);
    if (!Number.isFinite(strike)) continue;
    const iv = row.volatility;
    if (typeof iv !== 'number' || !Number.isFinite(iv)) continue;

    const entry: ChainEntry = { iv };
    for (const key of Object.keys(row)) {
      if (key === 'strike' || key === 'volatility') continue;
      const side = parseChainSide(row[key]);
      if (!side) continue;
      if (key.endsWith('-C')) entry.call = side;
      else if (key.endsWith('-P')) entry.put = side;
    }

    out[String(strike)] = entry;
  }

  return Object.keys(out).length > 0 ? out : null;
}

// True when the requested expiry was genuinely calibrated at this minute - i.e. the
// date is a key of result.surface.expiry.calibrations. Absent => the API silently
// interpolated a smile => untrusted. PURE.
export function isExpiryCalibrated(result: any, expiryDate: string): boolean {
  const cals = result?.surface?.expiry?.calibrations;
  if (!cals || typeof cals !== 'object') return false;
  return Object.prototype.hasOwnProperty.call(cals, expiryDate);
}

// The two timestamp shapes seen on this endpoint: the backend's value_date form
// ("YYYY-MM-DD HH:mm:ss") and plain ISO 8601 - parsed strictly (no silent
// fallback to JS Date() for an unrecognised shape, which moment.utc(x) alone
// would otherwise do with a console warning).
const TIMESTAMP_FORMATS = ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601];

// True when two timestamp-ish strings denote the SAME instant, parsed with
// moment.utc rather than compared as strings - so a format difference ('T' vs
// space separator, a trailing 'Z', etc) is never mistaken for a different time.
// Invalid/non-string/unparseable input never matches. PURE.
export function sameInstant(a: unknown, b: unknown): boolean {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  const ma = moment.utc(a, TIMESTAMP_FORMATS, true);
  const mb = moment.utc(b, TIMESTAMP_FORMATS, true);
  return ma.isValid() && mb.isValid() && ma.isSame(mb);
}

// The one echo signal worth distrusting: T1 and T2 come back with the IDENTICAL
// returned timestamp despite having been REQUESTED at different minutes. A
// legitimate nearest-available snap still tracks each request, so two distinct
// requested minutes snapping to one and the same stored minute is not the
// expected case; value_date being silently ignored (same live/latest smile
// returned for both calls) produces exactly this signature. Only flagged once
// both endpoints have resolved. PURE.
export function chainEndpointsSuspect(
  t1: ChainEndpoint | null,
  t2: ChainEndpoint | null,
  requestedT1Iso: string,
  requestedT2Iso: string,
): boolean {
  if (!t1 || !t2) return false;
  if (sameInstant(formatValueDate(requestedT1Iso), formatValueDate(requestedT2Iso))) return false;
  return sameInstant(t1.timestamp, t2.timestamp);
}

// Per-strike IV(T2) - IV(T1), fractions, over the strikes present on BOTH sides,
// but ONLY when BOTH endpoints are trusted (calibrated). A non-finite IV on either
// side omits that strike (never zeroed); an untrusted/absent endpoint yields no
// delta at all. PURE.
export function computeChainDelta(
  t1: ChainEndpoint | null,
  t2: ChainEndpoint | null,
): Record<string, number> | null {
  if (!t1 || !t2 || !t1.trusted || !t2.trusted) return null;

  const delta: Record<string, number> = {};
  for (const key of Object.keys(t2.strikes)) {
    const a = t1.strikes[key];
    const b = t2.strikes[key];
    if (!a || !b) continue;
    if (Number.isFinite(a.iv) && Number.isFinite(b.iv)) delta[key] = b.iv - a.iv;
  }

  return Object.keys(delta).length > 0 ? delta : null;
}

// A fetchable selection exists: market/T1/T2/selectedExpiry are all set and the
// surface carries an instrument chain ('generated' surfaces never do). Used to
// gate BOTH a selection-change fetch and a live poll. PURE.
export function isValidChainSelection(params: {
  surfaceType: 'exchange' | 'generated' | undefined;
  market: string;
  t1: string;
  t2: string;
  selectedExpiry: string;
}): boolean {
  const { surfaceType, market, t1, t2, selectedExpiry } = params;
  if (surfaceType === 'generated') return false;
  return Boolean(market && t1 && t2 && selectedExpiry);
}

// Whether the live 30s repoll should be armed: a valid selection exists AND T2
// is live. A fixed historic T1/T2 pair (t2IsLive false) is static data - it is
// fetched once on selection but never polled. PURE.
export function shouldPollChain(params: {
  t2IsLive: boolean;
  surfaceType: 'exchange' | 'generated' | undefined;
  market: string;
  t1: string;
  t2: string;
  selectedExpiry: string;
}): boolean {
  return params.t2IsLive && isValidChainSelection(params);
}

// The ISO actually requested for T2 in a generation: the stored t2 when
// historic, or `nowIso` (the caller's moment.utc() at fetch time) when live -
// so a live poll's value_date tracks the CURRENT instant, never the
// click-time snapshot in `t2`. T1 has no live concept - it always uses the
// stored t1 directly, no helper needed. PURE given `nowIso`.
export function resolveT2RequestIso(t2: string, t2IsLive: boolean, nowIso: string): string {
  return t2IsLive ? nowIso : t2;
}

// The ordered (ascending by strike) row universe, contributed only by TRUSTED
// endpoints - an untrusted side adds no rows, so when both are untrusted the chain
// is empty rather than a wall of blank rows. PURE.
export function selectChainStrikeRows(
  t1: ChainEndpoint | null,
  t2: ChainEndpoint | null,
): string[] {
  const keys = new Set<string>();
  const add = (e: ChainEndpoint | null) => {
    if (!e || !e.trusted) return;
    Object.keys(e.strikes).forEach((k) => keys.add(k));
  };
  add(t1);
  add(t2);
  return Array.from(keys).sort((a, b) => Number(a) - Number(b));
}

export const MarketChangeChainProvider = (props: IMarketChangeChainParamsProvider) => {
  const { children } = props;

  const { sendMessage, subscribeToChannel, unsubscribeFromChannel } = useContext(WebSocketContext);
  const { market, surfaceType, t1, t2, t2IsLive } = useContext(MarketChangeContext);
  const { expiries } = useListedExpiries(market);

  const [selectedExpiry, setSelectedExpiry] = usePersistentState<string>(
    STATE_PATH,
    'selectedExpiry',
    '',
  );
  const [endpointT1, setEndpointT1] = useState<ChainEndpoint | null>(null);
  const [endpointT2, setEndpointT2] = useState<ChainEndpoint | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default / repair the selected expiry: when the list resolves and the current
  // selection is not in it (unset, or a stale expiry from another token), pick the
  // nearest listed expiry.
  useEffect(() => {
    if (expiries.length === 0) return;
    if (expiries.includes(selectedExpiry)) return;
    setSelectedExpiry(expiries[0]);
  }, [expiries, selectedExpiry, setSelectedExpiry]);

  const genRef = useRef(0);
  const inFlightRef = useRef<Set<string>>(new Set());
  const subscribedRef = useRef<Set<string>>(new Set());
  const pendingRef = useRef<Set<ChainSlot>>(new Set());
  const handleOiRef = useRef<(gen: number, res: any) => void>(() => {});
  const handleIvsRef = useRef<(slot: ChainSlot, gen: number, res: any) => void>(() => {});

  // The ISO actually requested for each slot in the CURRENT generation: T1 is
  // always the stored t1; T2 is the stored t2 when historic, or a fresh
  // moment.utc() "now" on every live fetch (initial + each poll) - never the
  // click-time snapshot in `t2`. handleOi/handleIvs/echoSuspect read this
  // instead of the raw context value, so a live poll's echo guard and
  // displayed timestamp compare against what was ACTUALLY sent.
  const requestedIsoRef = useRef<{ T1: string; T2: string }>({ T1: t1, T2: t2 });

  // Latest unsubscribeFromChannel, read only by the unmount-only cleanup
  // effect below so that effect can have an empty dep array (mount/unmount
  // only) without resubscribing on every context identity change.
  const unsubscribeFromChannelRef = useRef(unsubscribeFromChannel);
  useEffect(() => {
    unsubscribeFromChannelRef.current = unsubscribeFromChannel;
  }, [unsubscribeFromChannel]);

  const markResolved = useCallback((slot: ChainSlot) => {
    pendingRef.current.delete(slot);
    if (pendingRef.current.size === 0) setLoading(false);
  }, []);

  const setEndpoint = useCallback((slot: ChainSlot, endpoint: ChainEndpoint | null) => {
    if (slot === 'T1') setEndpointT1(endpoint);
    else setEndpointT2(endpoint);
  }, []);

  // Fire the two historic IVS calls for the resolved strike set. value_date is the
  // backend's "YYYY-MM-DD HH:MM:SS" form of each timestamp; the strike list is
  // IDENTICAL across both endpoints so the chains align strike-for-strike.
  const getSmile = useCallback(
    (slot: ChainSlot, iso: string, strikes: number[], gen: number) => {
      const channel = `${KEY}_${slot}_${gen}`;
      if (inFlightRef.current.has(channel)) return;
      inFlightRef.current.add(channel);

      const { base, quote } = parseTokenPair(market);
      const request = {
        id: channel,
        method: 'implied_volatility_smile',
        params: {
          base,
          quote,
          value_date: formatValueDate(iso),
          expiry: expiryToParam(selectedExpiry),
          strikes,
          deltas: [0.5],
          model: 'svi_jw',
        },
        jsonrpc: '2.0',
      };

      sendMessage(JSON.stringify(request));
      subscribeToChannel(channel, (res: any) => handleIvsRef.current(slot, gen, res));
      subscribedRef.current.add(channel);
    },
    [market, selectedExpiry, sendMessage, subscribeToChannel],
  );

  // OI response: resolve the strike universe for the selected expiry, then fan out
  // to the two IVS calls. No strikes -> nothing to query (empty, not an error row).
  const handleOi = useCallback(
    (gen: number, res: any) => {
      if (gen !== genRef.current) return;
      inFlightRef.current.delete(`${KEY}_OI_${gen}`);

      if (!res || res.error || !res.result) {
        setError('Failed to load the strike universe');
        setLoading(false);
        return;
      }

      const strikes = selectExpiryStrikes(res.result, selectedExpiry);
      if (strikes.length === 0) {
        setLoading(false);
        return;
      }

      pendingRef.current = new Set<ChainSlot>(['T1', 'T2']);
      getSmile('T1', requestedIsoRef.current.T1, strikes, gen);
      getSmile('T2', requestedIsoRef.current.T2, strikes, gen);
    },
    [selectedExpiry, getSmile],
  );

  // IVS response: a hard error discards the endpoint; otherwise the calibration
  // gate decides trust and we keep whatever timestamp the endpoint actually
  // echoed (the nearest-available snapshot, not necessarily the requested
  // minute) as that endpoint's as-of time - see the echo-guard note above.
  const handleIvs = useCallback(
    (slot: ChainSlot, gen: number, res: any) => {
      if (gen !== genRef.current) return;
      inFlightRef.current.delete(`${KEY}_${slot}_${gen}`);

      if (!res || res.error || !res.result) {
        setError('Failed to load the options chain');
        setEndpoint(slot, null);
        markResolved(slot);
        return;
      }

      const iso = requestedIsoRef.current[slot];
      const echoedTimestamp = res.result.surface?.expiry?.timestamp;
      const trusted = isExpiryCalibrated(res.result, selectedExpiry);
      const strikes = parseChainStrikes(res.result) ?? {};
      const spot = res.result.market?.spot;
      setEndpoint(slot, {
        timestamp: typeof echoedTimestamp === 'string' ? echoedTimestamp : formatValueDate(iso),
        trusted,
        strikes,
        ...(typeof spot === 'number' && Number.isFinite(spot) ? { spot } : {}),
      });
      markResolved(slot);
    },
    [selectedExpiry, setEndpoint, markResolved],
  );

  useEffect(() => {
    handleOiRef.current = handleOi;
  }, [handleOi]);
  useEffect(() => {
    handleIvsRef.current = handleIvs;
  }, [handleIvs]);

  const getOi = useCallback(
    (gen: number) => {
      const channel = `${KEY}_OI_${gen}`;
      if (inFlightRef.current.has(channel)) return;
      inFlightRef.current.add(channel);

      const request = {
        id: channel,
        method: 'option_open_interest',
        params: { option_markets: [{ venue: 'deribit', token_pair: market }] },
        jsonrpc: '2.0',
      };

      sendMessage(JSON.stringify(request));
      subscribeToChannel(channel, (res: any) => handleOiRef.current(gen, res));
      subscribedRef.current.add(channel);
    },
    [market, sendMessage, subscribeToChannel],
  );

  // Open a new generation and fire OI -> IVS(T1) -> IVS(T2). `reset`
  // distinguishes a SELECTION change (market/T1/T2/expiry/surfaceType -
  // clears the endpoints and shows loading, as before) from a live 30s POLL
  // (refreshes in place: endpoints and `loading` are left untouched so the
  // panel never flashes). T2's requested instant is the stored t2 when
  // historic, but recomputed as moment.utc() "now" on every call while
  // t2IsLive - so a poll tracks the actual current time, not the click-time
  // snapshot. The prior generation's channels are torn down before this one
  // opens; the unmount-only effect below handles the final teardown.
  const fetchChain = useCallback(
    (reset: boolean) => {
      subscribedRef.current.forEach((channel) => unsubscribeFromChannel(channel));
      subscribedRef.current.clear();
      inFlightRef.current.clear();
      pendingRef.current = new Set<ChainSlot>();

      genRef.current += 1;
      const gen = genRef.current;

      if (reset) {
        setEndpointT1(null);
        setEndpointT2(null);
        setError(null);
      }

      if (!isValidChainSelection({ surfaceType, market, t1, t2, selectedExpiry })) {
        if (reset) setLoading(false);
        return;
      }

      requestedIsoRef.current = {
        T1: t1,
        T2: resolveT2RequestIso(t2, t2IsLive, moment.utc().toISOString()),
      };

      if (reset) setLoading(true);
      getOi(gen);
    },
    [market, surfaceType, t1, t2, t2IsLive, selectedExpiry, getOi, unsubscribeFromChannel],
  );

  // SELECTION change (market / T1 / T2 / expiry / surfaceType) - new
  // generation, reset + loading, as before. Also fires on mount.
  useEffect(() => {
    fetchChain(true);
  }, [fetchChain]);

  // Live 30s repoll - ONLY while t2IsLive and a valid selection exists. A
  // fixed historic T1/T2 pair (t2IsLive false) is static, so no interval is
  // set. Cleared on any dependency change (the effect above already fetches a
  // fresh generation for those) and on unmount.
  useEffect(() => {
    if (!shouldPollChain({ t2IsLive, surfaceType, market, t1, t2, selectedExpiry })) {
      return undefined;
    }

    const interval = setInterval(() => fetchChain(false), POLL_MS);
    return () => clearInterval(interval);
  }, [t2IsLive, surfaceType, market, t1, t2, selectedExpiry, fetchChain]);

  // Unmount-only teardown. The generation churn above tears down the PRIOR
  // generation's channels itself at the start of every fetchChain call; this
  // is only for the final unsubscribe when the provider itself unmounts.
  useEffect(() => {
    return () => {
      subscribedRef.current.forEach((channel) => unsubscribeFromChannelRef.current(channel));
      subscribedRef.current.clear();
    };
  }, []);

  // See chainEndpointsSuspect: both endpoints echoing the identical timestamp
  // despite T1/T2 being requested at different minutes means value_date was
  // likely ignored for one side. Gates the dIV only - each side's own IV is
  // still gated solely by its own calibration check.
  const echoSuspect = useMemo(
    () =>
      chainEndpointsSuspect(
        endpointT1,
        endpointT2,
        requestedIsoRef.current.T1,
        requestedIsoRef.current.T2,
      ),
    [endpointT1, endpointT2],
  );

  useEffect(() => {
    if (echoSuspect) {
      setError('T1 and T2 returned the same surface timestamp for different requested times');
    }
  }, [echoSuspect]);

  const chainDelta = useMemo(
    () => (echoSuspect ? null : computeChainDelta(endpointT1, endpointT2)),
    [echoSuspect, endpointT1, endpointT2],
  );
  const strikeRows = useMemo(
    () => selectChainStrikeRows(endpointT1, endpointT2),
    [endpointT1, endpointT2],
  );

  const value = useMemo<IMarketChangeChainProvider>(
    () => ({
      endpointT1,
      endpointT2,
      chainDelta,
      strikeRows,
      expiries,
      selectedExpiry,
      setSelectedExpiry,
      surfaceType,
      loading,
      error,
    }),
    [
      endpointT1,
      endpointT2,
      chainDelta,
      strikeRows,
      expiries,
      selectedExpiry,
      setSelectedExpiry,
      surfaceType,
      loading,
      error,
    ],
  );

  return (
    <MarketChangeChainContext.Provider value={value}>{children}</MarketChangeChainContext.Provider>
  );
};
